/**
 * Type-safe обёртки модуля МЕНЕДЖЕР (Спринт 11): персонал, графики, заявки.
 *
 * Эндпойнты бэка (роутеры internal/<module>/interfaces/http/router.go):
 *
 *   Персонал (RBAC: staffPlus — owner/manager/staff/...):
 *     GET  /v1/staff/departments                 — дерево отделов { items }
 *     GET  /v1/staff/members?department={uuid}    — сотрудники отдела { items }
 *     POST /v1/staff/members                      — завести/пригласить (201)
 *
 *   Графики смен (RBAC: authenticated; management surface):
 *     GET  /v1/scheduling/schedules?department={uuid}  — графики (массив)
 *     GET  /v1/scheduling/schedules/{id}/shifts        — смены графика (массив)
 *
 *   Лента заявок менеджеру (RBAC: manager/owner — /v1/admin/requests
 *     с role-exception, см. cmd/api/main.go RequirePlatformAdminWithRole…):
 *     GET  /v1/admin/requests?state=&category=&limit=&offset=  — { items, count }
 *
 * ListStaff и ListSchedules ТРЕБУЮТ ?department=<uuid> (бэк 400 без него) —
 * поэтому обёртки списка персонала/графиков сначала тянут отделы, затем
 * агрегируют по каждому. Это делает useStaff/useSchedules, а здесь —
 * атомарные вызовы.
 */
import { customFetch } from './openapi-fetch.js';
import type {
  AdminRequestCreateInput,
  ManagerRequest,
  ManagerRequestsFilters,
  ManagerRequestsResponse,
  RequestView,
  Schedule,
  SchedulesResponse,
  Shift,
  ShiftsResponse,
  StaffDepartment,
  StaffDepartmentsResponse,
  StaffMember,
  StaffMemberCreateInput,
  StaffMembersResponse,
  StaffTgBindLinkResponse,
  ShiftTemplate,
  ShiftTemplateInput,
  ShiftTemplatesResponse,
  ShiftCreateInput,
  Shift as ShiftType,
  StaffMemberUpdateInput,
  StaffMemberTerminateInput,
  DepartmentCreateInput,
  DepartmentUpdateInput,
  Role,
  RolesResponse,
  RoleCreateInput,
  RoleUpdateInput,
} from '@hsc/types';

/* ===================== Персонал ===================== */

/** GET /v1/staff/departments — дерево отделов. */
export async function listStaffDepartments(): Promise<StaffDepartmentsResponse> {
  const raw = await customFetch<{ items?: StaffDepartment[] }>({
    url: '/staff/departments',
    method: 'GET',
  });
  return { items: raw.items ?? [] };
}

/** GET /v1/staff/members?department={id} — сотрудники одного отдела. */
export async function listStaffMembers(departmentId: string): Promise<StaffMembersResponse> {
  const raw = await customFetch<{ items?: StaffMember[] }>({
    url: '/staff/members',
    method: 'GET',
    params: { department: departmentId },
  });
  return { items: raw.items ?? [] };
}

/** POST /v1/staff/members — завести/пригласить сотрудника. */
export async function createStaffMember(input: StaffMemberCreateInput): Promise<StaffMember> {
  return customFetch<StaffMember>({ url: '/staff/members', method: 'POST', data: input });
}

/** POST /v1/staff/departments — создать отдел. */
export async function createStaffDepartment(input: DepartmentCreateInput): Promise<StaffDepartment> {
  return customFetch<StaffDepartment>({ url: '/staff/departments', method: 'POST', data: input });
}

/** PATCH /v1/staff/departments/{id} — переименовать/обновить отдел. */
export async function updateStaffDepartment(
  id: string,
  input: DepartmentUpdateInput,
): Promise<StaffDepartment> {
  return customFetch<StaffDepartment>({
    url: `/staff/departments/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: input,
  });
}

/** DELETE /v1/staff/departments/{id} — архивировать отдел. */
export async function archiveStaffDepartment(id: string, reason?: string): Promise<void> {
  await customFetch<unknown>({
    url: `/staff/departments/${encodeURIComponent(id)}`,
    method: 'DELETE',
    ...(reason ? { data: { reason } } : {}),
  });
}

/** GET /v1/staff/members/{id} — карточка сотрудника. */
export async function getStaffMember(id: string): Promise<StaffMember> {
  return customFetch<StaffMember>({
    url: `/staff/members/${encodeURIComponent(id)}`,
    method: 'GET',
  });
}

/** PATCH /v1/staff/members/{id} — правка отдела/должности сотрудника. */
export async function updateStaffMember(
  id: string,
  input: StaffMemberUpdateInput,
): Promise<StaffMember> {
  return customFetch<StaffMember>({
    url: `/staff/members/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: input,
  });
}

/** POST /v1/staff/members/{id}/terminate — увольнение сотрудника. */
export async function terminateStaffMember(
  id: string,
  input: StaffMemberTerminateInput,
): Promise<void> {
  await customFetch<unknown>({
    url: `/staff/members/${encodeURIComponent(id)}/terminate`,
    method: 'POST',
    data: input,
  });
}

/**
 * POST /v1/staff/{userID}/telegram/bind-link (#88) — выпуск одноразового
 * deep-link/QR для привязки Telegram-аккаунта сотрудника (вход через
 * бот/Mini App). userID — platform.users_identity.id (StaffMember.user_id).
 * 409 если у отеля не настроен Telegram-бот.
 */
export async function issueStaffTgBindLink(userID: string): Promise<StaffTgBindLinkResponse> {
  return customFetch<StaffTgBindLinkResponse>({
    url: `/staff/${encodeURIComponent(userID)}/telegram/bind-link`,
    method: 'POST',
  });
}

/** Результат авто-логина сотрудника по Telegram initData (#88). */
export type StaffTgWebAppResult =
  | {
      outcome: 'authorized';
      access_token: string;
      refresh_token?: string;
      roles?: string[];
    }
  | { outcome: 'not_bound' };

/**
 * POST /v1/auth/staff-tg/webapp — авто-логин ПЕРСОНАЛА в Mini App по initData.
 * Тенант резолвится по поддомену (X-Tenant-Slug), JWT не нужен (noAuth). При
 * 200 отдаёт staff-сессию с РЕАЛЬНЫМИ ролями сотрудника; 404 staff_tg.not_bound
 * → не привязан (фронт пробует гостевой вход / обычный логин).
 */
export async function staffTgWebApp(initData: string): Promise<StaffTgWebAppResult> {
  try {
    const body = await customFetch<{
      access_token: string;
      refresh_token?: string;
      roles?: string[];
    }>({
      url: '/auth/staff-tg/webapp',
      method: 'POST',
      data: { init_data: initData },
      noAuth: true,
    });
    return { outcome: 'authorized', ...body };
  } catch (err) {
    const e = err as { status?: number; body?: { title?: string } };
    if (e.status === 404 && e.body?.title === 'staff_tg.not_bound') {
      return { outcome: 'not_bound' };
    }
    throw err;
  }
}

/* ===================== Роли (RBAC, §7.4) ===================== */

/** GET /v1/admin/roles?tenant_id=<uuid> — каталог ролей тенанта. */
export async function listRoles(tenantId: string): Promise<RolesResponse> {
  const raw = await customFetch<{ items?: Role[]; count?: number }>({
    url: '/admin/roles',
    method: 'GET',
    params: { tenant_id: tenantId },
  });
  return { items: raw.items ?? [], count: raw.count ?? raw.items?.length ?? 0 };
}

/**
 * POST /v1/admin/roles — создать кастомную роль тенанта.
 * permissions — ВСЕГДА массив (даже если пустой даём []), иначе бэк/RBAC
 * молча выкидывает роль (feedback_malformed_role_perms_drops_rbac).
 */
export async function createRole(input: RoleCreateInput): Promise<Role> {
  return customFetch<Role>({
    url: '/admin/roles',
    method: 'POST',
    data: { ...input, permissions: input.permissions ?? [] },
  });
}

/** PATCH /v1/admin/roles/{id} — обновить набор прав роли. */
export async function updateRole(id: string, input: RoleUpdateInput): Promise<Role> {
  return customFetch<Role>({
    url: `/admin/roles/${encodeURIComponent(id)}`,
    method: 'PATCH',
    data: { permissions: input.permissions ?? [] },
  });
}

/** DELETE /v1/admin/roles/{id} — удалить кастомную роль. */
export async function deleteRole(id: string): Promise<void> {
  await customFetch<unknown>({
    url: `/admin/roles/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}

/* ===================== Графики смен ===================== */

/** GET /v1/scheduling/schedules?department={id} — графики отдела (массив). */
export async function listSchedules(departmentId: string): Promise<SchedulesResponse> {
  const raw = await customFetch<Schedule[]>({
    url: '/scheduling/schedules',
    method: 'GET',
    params: { department: departmentId },
  });
  return { items: Array.isArray(raw) ? raw : [] };
}

/** GET /v1/scheduling/schedules/{id}/shifts — смены графика (массив). */
export async function listScheduleShifts(scheduleId: string): Promise<ShiftsResponse> {
  const raw = await customFetch<Shift[]>({
    url: `/scheduling/schedules/${encodeURIComponent(scheduleId)}/shifts`,
    method: 'GET',
  });
  return { items: Array.isArray(raw) ? raw : [] };
}

/**
 * GET /v1/me/shifts — СВОИ смены текущего сотрудника (staff-portal).
 * Бэк резолвит staff_member_id из JWT (см. NewStaffMemberResolver), отдаёт
 * только смены вызывающего. Опциональное окно [from, to) (ISO-8601).
 * Пустой массив (нет назначенных смен) — это норма, не ошибка.
 */
export async function listMyShifts(window?: {
  from?: string;
  to?: string;
}): Promise<ShiftsResponse> {
  const params: Record<string, string> = {};
  if (window?.from) params['from'] = window.from;
  if (window?.to) params['to'] = window.to;
  const raw = await customFetch<Shift[]>({
    url: '/me/shifts',
    method: 'GET',
    params,
  });
  return { items: Array.isArray(raw) ? raw : [] };
}

/* ===================== Лента заявок ===================== */

/** GET /v1/admin/requests — лента заявок отеля для менеджера/владельца. */
export async function listManagerRequests(
  filters?: ManagerRequestsFilters,
): Promise<ManagerRequestsResponse> {
  const params: Record<string, string | number> = {};
  if (filters?.state) params['state'] = filters.state;
  if (filters?.category) params['category'] = filters.category;
  if (filters?.limit !== undefined) params['limit'] = filters.limit;
  if (filters?.offset !== undefined) params['offset'] = filters.offset;
  const raw = await customFetch<{ items?: ManagerRequest[]; count?: number }>(
    Object.keys(params).length > 0
      ? { url: '/admin/requests', method: 'GET', params }
      : { url: '/admin/requests', method: 'GET' },
  );
  return { items: raw.items ?? [], count: raw.count ?? raw.items?.length ?? 0 };
}

/**
 * POST /v1/admin/requests — создание заявки персоналом/менеджером («+ Заявка»,
 * канбан #34). RBAC: role-exception в cmd/api/main.go допускает management+staff.
 * Возвращает созданную заявку (RequestView), которая попадёт в колонку «Новые».
 */
export async function adminCreateRequest(input: AdminRequestCreateInput): Promise<RequestView> {
  return customFetch<RequestView>({ url: '/admin/requests', method: 'POST', data: input });
}

/* ============ Переходы статуса заявки (канбан #34) ============ */
/*
 * Менеджер/админ (front_desk) перетаскивает карточку между колонками
 * канбана → смена статуса заявки. Бэк: internal/request/interfaces/http,
 * под /v1/requests/{id}/* (actorIsStaff пускает staff/management):
 *   POST /v1/requests/{id}/start    → state in_progress («В работе»)
 *   POST /v1/requests/{id}/resolve  → state resolved   («Выполнено»)
 *   POST /v1/requests/{id}/cancel   → state cancelled   («Отменено»)
 * Ответ — обновлённая заявка (RequestView).
 */

/** POST /v1/requests/{id}/start — взять заявку в работу. */
export async function startManagerRequest(id: string): Promise<RequestView> {
  return customFetch<RequestView>({
    url: `/requests/${encodeURIComponent(id)}/start`,
    method: 'POST',
    data: {},
  });
}

/** POST /v1/requests/{id}/resolve — отметить заявку выполненной. */
export async function resolveManagerRequest(
  id: string,
  resolutionNote?: string,
): Promise<RequestView> {
  return customFetch<RequestView>({
    url: `/requests/${encodeURIComponent(id)}/resolve`,
    method: 'POST',
    data: resolutionNote ? { resolution_note: resolutionNote } : {},
  });
}

/** POST /v1/requests/{id}/cancel — отменить заявку. */
export async function cancelManagerRequest(id: string, reason?: string): Promise<RequestView> {
  return customFetch<RequestView>({
    url: `/requests/${encodeURIComponent(id)}/cancel`,
    method: 'POST',
    data: reason ? { reason } : {},
  });
}

/**
 * PATCH /v1/admin/requests/{id}/status — management force-transition в ЛЮБОЙ
 * статус (канбан #34). В отличие от start/resolve/cancel, бэк НЕ проверяет
 * строгий FSM (нет 412): менеджер/владелец/админ перетаскивает карточку в
 * любую колонку, включая «назад». RBAC — role-exception в cmd/api/main.go.
 *   status ∈ created | assigned | in_progress | resolved | cancelled | escalated
 */
export async function forceManagerRequestStatus(
  id: string,
  status: string,
  reason?: string,
): Promise<RequestView> {
  return customFetch<RequestView>({
    url: `/admin/requests/${encodeURIComponent(id)}/status`,
    method: 'PATCH',
    data: reason ? { status, reason } : { status },
  });
}

/* ===================== Очередь F&B (кухня / бар) ===================== */

/**
 * Переходы жизненного цикла заявки для очереди персонала (кухня/бар/официант).
 * Бэк: internal/request — POST /v1/requests/{id}/assign|start|resolve.
 *   assign  — назначить заявку на сотрудника (staff_id), created → assigned;
 *   start   — взять в работу (готовить), assigned → in_progress;
 *   resolve — завершить (готово/подано), in_progress → resolved.
 * authorizeRequestAccess допускает персонал тенанта.
 */
export async function assignRequest(id: string, staffId: string): Promise<void> {
  await customFetch<unknown>({
    url: `/requests/${encodeURIComponent(id)}/assign`,
    method: "POST",
    data: { staff_id: staffId },
  });
}

/** POST /v1/requests/{id}/start — взять в работу (assigned → in_progress). */
export async function startRequest(id: string): Promise<void> {
  await customFetch<unknown>({
    url: `/requests/${encodeURIComponent(id)}/start`,
    method: "POST",
  });
}

/** POST /v1/requests/{id}/resolve — завершить (in_progress → resolved). */
export async function resolveRequest(id: string, note?: string): Promise<void> {
  await customFetch<unknown>({
    url: `/requests/${encodeURIComponent(id)}/resolve`,
    method: "POST",
    ...(note !== undefined && note !== "" ? { data: { resolution_note: note } } : {}),
  });
}

/** POST /v1/requests/{id}/deliver — отдать заказ гостю (resolved → delivered).
 * F&B-очередь §9.5, 4-я колонка DELIVERED. Только персонал (бэк 403 гостю). */
export async function deliverRequest(id: string): Promise<void> {
  await customFetch<unknown>({
    url: `/requests/${encodeURIComponent(id)}/deliver`,
    method: "POST",
  });
}

/* ===================== Шаблоны смен ===================== */

/** GET /v1/scheduling/shift-templates — шаблоны смен отеля (массив). */
export async function listShiftTemplates(
  includeInactive = false,
): Promise<ShiftTemplatesResponse> {
  const raw = await customFetch<ShiftTemplate[]>({
    url: '/scheduling/shift-templates',
    method: 'GET',
    ...(includeInactive ? { params: { include_inactive: 'true' } } : {}),
  });
  return { items: Array.isArray(raw) ? raw : [] };
}

/** POST /v1/scheduling/shift-templates — создать шаблон (201). */
export async function createShiftTemplate(input: ShiftTemplateInput): Promise<ShiftTemplate> {
  return customFetch<ShiftTemplate>({
    url: '/scheduling/shift-templates',
    method: 'POST',
    data: input,
  });
}

/** PUT /v1/scheduling/shift-templates/{id} — обновить шаблон. */
export async function updateShiftTemplate(
  id: string,
  input: ShiftTemplateInput,
): Promise<ShiftTemplate> {
  return customFetch<ShiftTemplate>({
    url: `/scheduling/shift-templates/${encodeURIComponent(id)}`,
    method: 'PUT',
    data: input,
  });
}

/** DELETE /v1/scheduling/shift-templates/{id} — удалить шаблон (204). */
export async function deleteShiftTemplate(id: string): Promise<void> {
  await customFetch<void>({
    url: `/scheduling/shift-templates/${encodeURIComponent(id)}`,
    method: 'DELETE',
  });
}

/** POST /v1/scheduling/shifts — добавить смену в график (201). */
export async function createShift(input: ShiftCreateInput): Promise<ShiftType> {
  return customFetch<ShiftType>({ url: '/scheduling/shifts', method: 'POST', data: input });
}
